import React, { useState, useEffect } from 'react';
import LanguageSelect from '../../components/LanguageSelect';
import api from '../../api/ApiClient';

const ASSET_TYPES = { dataset:'Dataset', llm:'LLM', image_model:'Image Model', audio_model:'Audio Model', cv_model:'CV Model', embedding:'Embedding', other_model:'Model', notebook:'Notebook', pipeline:'Pipeline' };
const MODALITIES  = ['text','image','audio','video','tabular','3d','multimodal','code','other'];

function Badge({ children, color, bg, border }) {
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, fontWeight: 600, color, background: bg, border: `1px solid ${border || bg}` }}>
      {children}
    </span>
  );
}

function RequestCard({ req, isOwn, onOffer, onAccept }) {
  const [showOffer, setShowOffer] = useState(false);
  const [price, setPrice] = useState('');
  const [msg, setMsg] = useState('');

  const send = async () => {
    if (!price) return;
    await onOffer(req._id, { price: Number(price), message: msg });
    setShowOffer(false); setPrice(''); setMsg('');
  };

  const statusBadge = {
    open:        <Badge color="#16A34A" bg="#F0FDF4" border="#BBF7D0">Open</Badge>,
    in_progress: <Badge color="#D97706" bg="#FFFBEB" border="#FDE68A">In progress</Badge>,
    fulfilled:   <Badge color="#1D4ED8" bg="#EFF6FF" border="#BFDBFE">Fulfilled</Badge>,
    cancelled:   <Badge color="#64748B" bg="#F1F5F9" border="#E2E8F0">Cancelled</Badge>,
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0, flex: 1, marginRight: 12 }}>{req.title}</h3>
        {statusBadge[req.status] || <Badge color="#64748B" bg="#F1F5F9">{req.status}</Badge>}
      </div>

      {req.description && (
        <p style={{ fontSize: 13, color: 'var(--text-2)', margin: '0 0 10px', lineHeight: 1.5 }}>{req.description}</p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {req.assetType && <Badge color="#1D4ED8" bg="#EFF6FF" border="#BFDBFE">{ASSET_TYPES[req.assetType] || req.assetType}</Badge>}
        {req.dataModality?.map(m => <Badge key={m} color="#6D28D9" bg="#F5F3FF" border="#DDD6FE">{m}</Badge>)}
        {req.languages?.map(l => (
          <span key={l} style={{ fontSize: 10, padding: '2px 6px', borderRadius: 3, background: 'var(--accent-bg)', color: 'var(--accent)', fontFamily: 'Space Mono', fontWeight: 700, textTransform: 'uppercase' }}>{l}</span>
        ))}
        {req.tags?.map(t => <span key={t} style={{ fontSize: 11, padding: '2px 7px', borderRadius: 3, background: 'var(--surface2)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>{t}</span>)}
      </div>

      <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'var(--text-3)', marginBottom: 12, flexWrap: 'wrap' }}>
        {req.budget   && <span>Budget: <b style={{ color: 'var(--text)', fontFamily: 'Space Mono' }}>${req.budget}</b></span>}
        {req.deadline && <span>Deadline: {new Date(req.deadline).toLocaleDateString()}</span>}
        <span>{req.offers?.length || 0} offer{req.offers?.length === 1 ? '' : 's'}</span>
      </div>

      {/* Owner: see offers */}
      {isOwn && req.offers?.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', marginBottom: 8 }}>Offers</div>
          {req.offers.map(o => (
            <div key={o._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 6, background: 'var(--surface2)', marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--text)' }}>{o.message || '(no message)'}</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {o.sellerId?.name || 'Seller'}
                  {o.sellerId?.sellerStats?.isVerifiedSeller && <span style={{ color: 'var(--success)', marginLeft: 4 }}>✓</span>}
                </div>
              </div>
              <span style={{ fontFamily: 'Space Mono', fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>${o.price}</span>
              {req.status === 'in_progress' && (
                <button onClick={() => onAccept(req._id, o._id)} style={{ padding: '4px 12px', borderRadius: 5, background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                  Accept
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Non-owner: make offer */}
      {!isOwn && req.status === 'open' && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 4 }}>
          {!showOffer ? (
            <button onClick={() => setShowOffer(true)} className="btn-secondary" style={{ fontSize: 13 }}>Make offer</button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="number" placeholder="Price $" value={price} onChange={e => setPrice(e.target.value)}
                  className="field" style={{ width: 120 }} />
                <input placeholder="Message (optional)" value={msg} onChange={e => setMsg(e.target.value)}
                  className="field" style={{ flex: 1 }} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={send} className="btn-primary" style={{ fontSize: 13 }}>Send</button>
                <button onClick={() => setShowOffer(false)} className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreateForm({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', assetType: '', dataModality: [], languages: [], budget: '', deadline: '', tags: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggle = m => set('dataModality', form.dataModality.includes(m) ? form.dataModality.filter(x => x !== m) : [...form.dataModality, m]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.createRequest({
        title: form.title, description: form.description,
        assetType: form.assetType || undefined,
        dataModality: JSON.stringify(form.dataModality),
        languages: JSON.stringify(form.languages),
        budget: form.budget ? Number(form.budget) : undefined,
        deadline: form.deadline || undefined,
        tags: JSON.stringify(form.tags.split(',').map(t => t.trim()).filter(Boolean)),
      });
      onCreated(); onClose();
    } catch (err) { alert(err?.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="card" style={{ padding: 24, marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 18 }}>New Data Request</h3>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl}>Title *</label>
          <input value={form.title} onChange={e => set('title', e.target.value)} required className="field" style={fw} placeholder="e.g. Medical CT scan dataset" />
        </div>
        <div>
          <label style={lbl}>Description</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="field" style={{ ...fw, resize: 'vertical' }} placeholder="Describe what you need…" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Asset type</label>
            <select value={form.assetType} onChange={e => set('assetType', e.target.value)} className="field" style={{ ...fw, cursor: 'pointer' }}>
              <option value="">Any type</option>
              {Object.entries(ASSET_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Budget ($)</label>
            <input type="number" min="0" value={form.budget} onChange={e => set('budget', e.target.value)} className="field" style={fw} placeholder="0 = open" />
          </div>
        </div>
        <div>
          <label style={lbl}>Data modality</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {MODALITIES.map(m => {
              const on = form.dataModality.includes(m);
              return (
                <button type="button" key={m} onClick={() => toggle(m)} style={{
                  padding: '4px 11px', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 500, textTransform: 'capitalize', fontFamily: 'inherit',
                  border: `1px solid ${on ? '#A78BFA' : 'var(--border)'}`,
                  background: on ? '#F5F3FF' : 'var(--surface)', color: on ? '#6D28D9' : 'var(--text-2)',
                }}>{m}</button>
              );
            })}
          </div>
        </div>
        <div>
          <label style={lbl}>Languages</label>
          <LanguageSelect value={form.languages} onChange={v => set('languages', v)} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Deadline</label>
            <input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} className="field" style={fw} />
          </div>
          <div>
            <label style={lbl}>Tags (comma-separated)</label>
            <input value={form.tags} onChange={e => set('tags', e.target.value)} className="field" style={fw} placeholder="nlp, annotated…" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="submit" className="btn-primary" style={{ fontSize: 13 }}>Create request</button>
          <button type="button" onClick={onClose} className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-2)', marginBottom: 5 };
const fw  = { width: '100%', boxSizing: 'border-box' };

export default function DataRequests() {
  const [tab, setTab] = useState('open');
  const [open, setOpen] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    const [od, md] = await Promise.all([
      api.listRequests().catch(() => ({ requests: [] })),
      api.getMyRequests().catch(() => ({ requests: [] })),
    ]);
    setOpen(od.requests || []);
    setMine(md.requests || []);
    setLoading(false);
  };
  useEffect(() => { loadAll(); }, []);

  const handleOffer  = async (id, data) => { try { await api.makeOffer(id, data); loadAll(); } catch { alert('Failed'); } };
  const handleAccept = async (id, oid)  => { try { await api.acceptOffer(id, oid); loadAll(); } catch { alert('Failed'); } };

  const list = tab === 'open' ? open : mine;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px' }}>Data Requests</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" style={{ fontSize: 13 }}>New request</button>
      </div>

      {showForm && <CreateForm onClose={() => setShowForm(false)} onCreated={loadAll} />}

      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {[['open', `Open (${open.length})`], ['my', `My requests (${mine.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            border: `1px solid ${tab === k ? 'var(--accent)' : 'var(--border)'}`,
            background: tab === k ? 'var(--accent-bg)' : 'var(--surface)',
            color: tab === k ? 'var(--accent)' : 'var(--text-2)',
          }}>{l}</button>
        ))}
      </div>

      {loading && <div style={{ color: 'var(--text-3)' }}>Loading…</div>}
      {!loading && list.length === 0 && (
        <div className="card" style={{ padding: '60px 24px', textAlign: 'center' }}>
          <p style={{ color: 'var(--text-2)' }}>{tab === 'open' ? 'No open requests.' : 'No requests yet.'}</p>
        </div>
      )}
      {!loading && list.map(r => (
        <RequestCard key={r._id} req={r} isOwn={tab === 'my'} onOffer={handleOffer} onAccept={handleAccept} />
      ))}
    </div>
  );
}
